import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./sentRequestsPage.css";
import { getSentRequests } from "../../../services/connectionAPI.ts";
import { getAllUsers } from "../../../services/authAPI";
import ProfileDetailsModal from "../matrimonyList/profileDetailsModal.tsx";

const SentRequestsPage = () => {
  const user = useSelector((state: any) => state.auth.user);

  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const ITEMS_PER_PAGE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const handleCloseProfile = () => {
    setOpenModal(false);
    setSelectedProfile(null);
  };

  const handleOpenProfile = (profile: any) => {
    setSelectedProfile(profile);
    setOpenModal(true);
  };

  const calculateAgeNum = (dob?: string): number => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;

    return age;
  };

  const calculateAge = (dob?: string): string | number => (!dob ? "-" : calculateAgeNum(dob));

  const getInitial = (name?: string) => (name ? name.charAt(0).toUpperCase() : "?");
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);

      const [reqRes, userRes] = await Promise.all([getSentRequests(), getAllUsers()]);

      const requestsData = reqRes?.requests || [];
      const users = userRes?.users || [];

      // ✅ only logged-in user requests
      const filtered = requestsData.filter((item: any) => item.sender_id === user.id);

      // ✅ merge receiver details
      const enriched = filtered.map((req: any) => {
        const receiver = users.find((u: any) => u.id === req.receiver_id);

        return {
          ...req,
          request_id: req.id,
          request_status: req.status,
          profile: receiver || null,
        };
      });

      setRequests(enriched);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading requests", err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) fetchRequests();
  }, [fetchRequests, user?.id]);

  const totalPages = Math.ceil(requests.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const paginatedRequests = requests.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className='sent-request-loader'>
        <div className='sent-request-spinner'></div>
        <p>Loading your requests...</p>
      </div>
    );
  }

  return (
    <>
      <div className='sent-requests-page-wrapper'>
        <div className='sent-requests-premium-page'>
          {/* HEADER */}
          <div className='sent-requests-header'>
            <div>
              <h1>My Sent Requests</h1>
              <p>Track all your connection requests in one place</p>
            </div>
          </div>

          {/* EMPTY */}
          {requests.length === 0 ? (
            <div className='sent-request-empty'>
              <img src='https://cdn-icons-png.flaticon.com/512/7486/7486740.png' alt='empty' />
              <h3>No Requests Yet</h3>
              <p>You haven’t sent any connection requests.</p>
            </div>
          ) : (
            <div className='sent-requests-grid'>
              {paginatedRequests.map((req) => {
                const profile = req.profile;

                return (
                  <div className='sent-request-card' key={req.id}>
                    {/* HEADER */}
                    <div className='sent-request-top'>
                      <div className='sent-request-profile'>
                        <img
                          src={"https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                          alt='profile'
                          className='sent-request-avatar'
                        />

                        <div>
                          <h3>{profile?.name || "Unknown User"}</h3>
                          <span>ID: {req.receiver_id}</span>
                        </div>
                      </div>

                      <span className={`sent-request-status ${req.status}`}>{req.status}</span>
                    </div>

                    {/* DETAILS */}
                    <div className='sent-request-details'>
                      <div className='sent-request-box'>
                        <label>Caste</label>
                        <p>{profile?.caste || "-"}</p>
                      </div>

                      <div className='sent-request-box'>
                        <label>Religion</label>
                        <p>{profile?.religion || "-"}</p>
                      </div>

                      <div className='sent-request-box sent-request-full'>
                        <label>Location</label>
                        <p>{profile?.resident_country || "-"}</p>
                      </div>
                    </div>

                    <br />

                    {/* VIEW PROFILE */}
                    {req.status === "accepted" && profile && (
                      <button className='view-btn' onClick={() => handleOpenProfile(profile)}>
                        View Profile
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {totalPages > 1 && (
            <div className='pagination'>
              <button className='page-btn' onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                «
              </button>

              <button className='page-btn' onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                &lt;
              </button>

              <span className='page-info'>
                Showing {startIndex + 1} - {Math.min(endIndex, requests.length)} of {requests.length}
              </span>

              <button
                className='page-btn'
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>

              <button
                className='page-btn'
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                »
              </button>
            </div>
          )}
        </div>
      </div>
      <ProfileDetailsModal
        open={openModal}
        onClose={handleCloseProfile}
        profile={selectedProfile}
        calculateAge={calculateAge}
        getInitial={getInitial}
        isAdminView={false}
      />
    </>
  );
};

export default SentRequestsPage;
